import { AlertTriangle, Eye, FileSpreadsheet, DollarSign } from 'lucide-react';

const stats = [
  { value: '$1.2B+', label: 'in CFTC fines since 2020' },
  { value: '73%', label: 'of traders still use spreadsheets' },
  { value: '4-8 hrs', label: 'daily manual aggregation' },
  { value: '35%', label: 'unknowingly breached limits' },
];

const painPoints = [
  {
    icon: FileSpreadsheet,
    title: 'Multi-Account Chaos',
    description: 'Juggling positions across multiple FCMs, accounts, and trading systems with error-prone spreadsheets.',
  },
  {
    icon: Eye,
    title: 'Real-Time Blindness',
    description: 'End-of-day reports mean you only know about breaches after they happen.',
  },
  {
    icon: AlertTriangle,
    title: 'Audit Nightmares',
    description: 'Scrambling to reconstruct compliance history when regulators come knocking.',
  },
  {
    icon: DollarSign,
    title: 'Costly Errors',
    description: 'A single position limit breach can result in six or seven-figure fines.',
  },
];

export function TheProblemSection() {
  return (
    <section id="the-problem" className="relative py-20 md:py-28 bg-slate-50">
      <div className="container relative">
        {/* Section Header */}
        <div className="mx-auto max-w-2xl text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-200/80 border border-slate-300/50 px-4 py-1.5 text-sm mb-6">
            <AlertTriangle className="h-4 w-4 text-slate-600" />
            <span className="text-slate-700 font-medium">The Industry Problem</span>
          </div>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4 text-slate-900">
            Position Limit Compliance Is{' '}
            <span className="text-purple-600">
              Broken
            </span>
          </h2>
          <p className="text-lg text-slate-600">
            Manual processes, fragmented data, and outdated tools put commodity traders at risk every day.
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="group rounded-2xl bg-white border border-slate-200 p-4 md:p-6 text-center shadow-sm hover:shadow-lg hover:border-purple-200 hover:-translate-y-1 transition-all duration-300"
            >
              <div className="text-2xl md:text-3xl font-bold text-purple-600 mb-1">
                {stat.value}
              </div>
              <div className="text-xs md:text-sm text-slate-600">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Pain Points Grid */}
        <div className="grid gap-6 sm:grid-cols-2">
          {painPoints.map((point) => (
            <div
              key={point.title}
              className="group rounded-2xl bg-white border border-slate-200 p-6 hover:border-purple-300 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
            >
              <div className="inline-flex rounded-xl bg-purple-100 p-3 mb-4 group-hover:bg-purple-200 transition-colors">
                <div className="rounded-lg bg-purple-600 p-2">
                  <point.icon className="h-5 w-5 text-white" />
                </div>
              </div>
              <h3 className="font-semibold mb-2 text-lg text-slate-900">{point.title}</h3>
              <p className="text-slate-600">{point.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
